/*
 * Copyright (c) CIRROM 2026.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import InsertLinkOutlinedIcon from "@mui/icons-material/InsertLinkOutlined";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import NavigateNext from "@mui/icons-material/NavigateNext";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import {
  AccountSummary,
  AccountSummaryCrmRecord,
  AccountSummaryEnrichSystem,
  AccountSummaryLinkageRow,
} from "../../api/accounts";
import {
  getCRMURLForSuggestedChange,
  getExplainForSuggestedChange,
  getSuggestedChangesForSystem,
} from "../../api/suggestedchanges";
import {
  cirromCandidatesForEntry,
  ACCOUNT_FIELDS,
  ExplainResponse,
  LedgerField,
  normalizeRecordReview,
} from "../record-review/helpers/normalize";
import { useSnackbar } from "notistack";
import { RecordReviewCard } from "../record-review/RecordReviewCard";
import { ShareCopyActions } from "../record-review/ShareCopyActions";
import { ValueOrEmpty } from "../record-review/LedgerRow";
import { useCrmVendor } from "../record-review/helpers/useCrmVendor";
import { getAccountUrl } from "../../api/enrichments";
import { CirromLogoAvatar } from "../Logo";
import { CirromLoadingLogo } from "../utils/CirromLoadingLogo";
import { toReadableText } from "../utils/utils";
import { EnrichTabContents } from "./EnrichTabContents";
import { searchTypeLabel } from "./helpers/sourceFields";
import classes from "./css/accountSummary.module.css";

const FieldGroup = (props: { label: string; children: React.ReactNode }) => (
  <div className={classes.fieldGroup}>
    <Typography
      variant="caption"
      component="h3"
      className={classes.fieldGroupLabel}
      sx={{ color: "text.secondary", fontWeight: 600 }}
    >
      {props.label}
    </Typography>
    {props.children}
  </div>
);

const SectionEmptyState = (props: { message: string }) => (
  <Typography
    variant="body2"
    sx={{ color: "text.secondary", fontStyle: "italic" }}
  >
    {props.message}
  </Typography>
);

/** Bare hostnames like "alpha.com" need a scheme before they can be opened. */
const toExternalUrl = (website: string) =>
  /^https?:\/\//i.test(website) ? website : `https://${website}`;

/*
 * Source logos shipped in public/logos. An allowlist rather than a
 * speculative URL, so a system without a shipped logo deterministically falls
 * back to its name in text. A logo matches either on the SystemType enum
 * (exact) or on a slug prefix of the tenant-chosen system name - prefix,
 * because tenants name systems with variants of the vendor ("Lexology
 * Compete") and the vendor's mark covers them all. `invertInDark` belongs to
 * each asset: a monochrome-ink mark inverts cleanly, a multi-colour one would
 * need its own dark treatment instead.
 */
const SOURCE_LOGOS: {
  file: string;
  invertInDark: boolean;
  slugPrefix?: string;
  types?: string[];
}[] = [
  { file: "lexology.svg", slugPrefix: "lexology", invertInDark: true },
  {
    file: "Companies_House.svg",
    types: ["UK_COMPANIES_HOUSE"],
    invertInDark: true,
  },
];

const resolveSourceLogo = (systemName?: string, systemType?: string) => {
  const slug = (systemName || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  const match = SOURCE_LOGOS.find(
    (logo) =>
      (logo.types && systemType && logo.types.includes(systemType)) ||
      (logo.slugPrefix &&
        (slug === logo.slugPrefix || slug.startsWith(`${logo.slugPrefix}-`)))
  );
  return match
    ? { src: `/logos/${match.file}`, invertInDark: match.invertInDark }
    : null;
};

/** Pending candidates: identity and provenance only, reviewed elsewhere. */
const ProposalRow = (props: { row: AccountSummaryLinkageRow }) => {
  const { row } = props;
  const detailParts = [
    row.datasource_id,
    searchTypeLabel(row.search_type),
  ].filter(Boolean);
  return (
    <Box className={classes.linkageRow} sx={{ borderColor: "divider" }}>
      <div className={classes.linkageRowText}>
        {row.company_name ? (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.company_name}
          </Typography>
        ) : (
          <ValueOrEmpty
            value={null}
            emptyTooltip="This proposed match arrived without a company name."
          />
        )}
        {detailParts.length > 0 && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {detailParts.join(" · ")}
          </Typography>
        )}
      </div>
    </Box>
  );
};

/* Raw ISO timestamps are serialisation, not information. */
const OVERVIEW_DATE_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

const formatOverviewDate = (value: string | null) => {
  if (!value) {
    return null;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? value : OVERVIEW_DATE_FORMATTER.format(parsed);
};

/*
 * The CRM record fields outside the review surface's seven comparison
 * fields. They join the model's full record - same grammar, same "Other"
 * group, counted by the completeness meter - with values read from the
 * explain payload when a ticket anchored one (fresh after a write) and from
 * the summary otherwise. All but website are excluded from ad-hoc editing in
 * AdHocEdit: identifiers, counts and timestamps are facts about the record,
 * not values a steward corrects.
 */
const ACCOUNT_EXTRA_FIELDS: {
  key: keyof AccountSummaryCrmRecord & string;
  label: string;
  format?: (value: string) => string;
}[] = [
  { key: "website", label: "Website" },
  { key: "company_type", label: "Type" },
  { key: "contact_manager", label: "Contact Manager" },
  {
    key: "created_time",
    label: "Created",
    format: (value) => formatOverviewDate(value) ?? value,
  },
  { key: "crm_account_id", label: "CRM Account Id" },
  { key: "external_identifier", label: "External Identifier" },
];

const accountExtraLedgerFields = (
  summary: AccountSummary,
  explain: ExplainResponse | null
): LedgerField[] => {
  const explainByField = new Map(
    (explain?.contact_data ?? []).map((entry) => [entry.field_name, entry])
  );
  return ACCOUNT_EXTRA_FIELDS.map((spec) => {
    const entry = explainByField.get(spec.key);
    const raw =
      entry !== undefined ? entry.crm_value : summary.crm[spec.key] ?? null;
    const value = raw == null ? null : String(raw);
    return {
      fieldName: spec.key,
      label: spec.label,
      crmValue: value != null && spec.format ? spec.format(value) : value,
      fromAccount: false,
      crmId: entry?.crm_id,
      /*
       * A pending change on one of these fields is as real as one on a
       * comparison field; the ticketed explain payload carries it here.
       */
      candidates: entry ? cirromCandidatesForEntry(entry) : [],
    };
  });
};

/*
 * Without a ticket there is no explain payload to fetch, but the surface
 * should not collapse to a different page: the account summary already
 * carries the CRM record, so it is shaped into the same payload and the
 * review surface renders with an empty ledger - exactly what a contact with
 * nothing pending gets. The roster comes from normalize's own ACCOUNT_FIELDS
 * so the two can never drift; fields the summary payload does not carry
 * (street, zip) get no entry and render as unknown rather than claiming the
 * CRM holds nothing - see the valueUnknown patch in the model.
 */
const summaryCarriesField = (summary: AccountSummary, key: string) =>
  key in summary.crm;

const synthesizeAccountExplain = (
  summary: AccountSummary
): ExplainResponse => ({
  entity_type: "ACCOUNT",
  record_id: summary.crm.id,
  crm_account_id: summary.crm.crm_account_id,
  integrated_system_id: summary.system_id,
  contact_data: ACCOUNT_FIELDS.filter((spec) =>
    summaryCarriesField(summary, spec.key)
  ).map((spec) => ({
    field_name: spec.key,
    crm_value:
      (summary.crm as Record<string, any>)[spec.key] == null
        ? null
        : String((summary.crm as Record<string, any>)[spec.key]),
    cirrom_changes: [],
  })),
});

/*
 * The strip is the triage board: each tab carries its source's verdict so a
 * steward reads the board without clicking through. Only states that need
 * attention or confirmation get ink; out of scope and idle stay bare, with
 * their words in the tooltip and the tab's accessible name.
 */
const sourceTabVerdict = (system: AccountSummaryEnrichSystem) => {
  const linked = system.in_scope && (system.linkage_matched ?? []).length > 0;
  const proposals = system.in_scope
    ? (system.linkage_proposals ?? []).length
    : 0;
  const words = !system.in_scope
    ? "out of enrich scope"
    : [
        linked ? "linked" : null,
        proposals > 0
          ? `${proposals} proposed ${proposals === 1 ? "match" : "matches"}`
          : null,
      ]
        .filter(Boolean)
        .join(", ") || "no matches yet";
  return { linked, proposals, words };
};

const sourceTabName = (system: AccountSummaryEnrichSystem) =>
  `${system.enrich_system_name || "Enrichment"}, ${
    sourceTabVerdict(system).words
  }`;

/*
 * The account's own CRM record - the default tab, on par with the record
 * review surface. The embedded RecordReviewCard always renders: dossier,
 * pending-changes ledger, full record. With a data-quality ticket
 * (ACCOUNT-entity suggested change) the explain payload anchors on it and
 * every write - approve, reject, the ad-hoc pencils - works exactly as it
 * does for a contact. Without one the model is synthesised from the summary
 * payload and the ledger states that nothing is waiting, but the pencils stay
 * live: the ad-hoc write anchors on the record and its system when there is
 * no originating change, so an account with a quiet queue is still editable.
 *
 * The ticket lookup goes through the grid endpoint filtered to ACCOUNT
 * entities and is narrowed to this account client-side - the endpoint has no
 * per-record filter.
 */
const AccountOverviewPanel = (props: {
  summary: AccountSummary;
  /** The enrich sync groups behind the source tabs; empty when none exist. */
  systems: AccountSummaryEnrichSystem[];
  anchorChangeId?: number | null;
  onChanged?: (decidedChangeIds?: number[]) => void;
  /** Write-in-flight signal from the embedded card, passed straight through. */
  onBusyChange?: (busy: boolean) => void;
  /** True once the ticket lookup has decided which Overview this is. */
  onSettledChange?: (settled: boolean) => void;
  /** The card footer's slot the embedded card renders its commit bar into. */
  commitActionsContainer?: HTMLElement | null;
}) => {
  const { summary } = props;
  /* A non-null explain IS the loaded ticket; the state only tracks lookup. */
  const [ticketState, setTicketState] = useState<"checking" | "none">(
    "checking"
  );
  const [explain, setExplain] = useState<ExplainResponse | null>(null);
  const [discoveredChangeId, setDiscoveredChangeId] = useState<number | null>(
    null
  );
  const [crmUrls, setCrmUrls] = useState<{
    contact?: string;
    account?: string;
  } | null>(null);

  /* The caller's anchor wins: it is the row the steward opened. */
  const activeChangeId = props.anchorChangeId ?? discoveredChangeId;

  /* The explain route 404s without a batch or system anchor. */
  const loadExplain = (changeId: number) =>
    getExplainForSuggestedChange(changeId, null, null, summary.system_id).then(
      setExplain
    );

  useEffect(() => {
    let cancelled = false;
    setTicketState("checking");
    setExplain(null);
    setCrmUrls(null);

    if (props.anchorChangeId) {
      /*
       * The modal already knows which ticket this is, so the ACCOUNT-wide
       * grid query - which has no per-record filter and is narrowed
       * client-side - is work with a known answer.
       */
      getCRMURLForSuggestedChange(props.anchorChangeId)
        .then((urls) => {
          if (!cancelled)
            setCrmUrls({ account: urls?.account ?? urls?.contact });
        })
        .catch(() => {
          // Links are a convenience; the record stands without them.
        });
      loadExplain(props.anchorChangeId).catch(() => setTicketState("none"));
      return () => {
        cancelled = true;
      };
    }

    getSuggestedChangesForSystem(
      summary.system_id,
      undefined,
      true,
      undefined,
      undefined,
      undefined,
      false,
      undefined,
      "ACCOUNT"
    )
      .then((rows: any[]) => {
        if (cancelled) return null;
        /*
         * The grid endpoint parks an ACCOUNT row's account id under the
         * crm_contact_id key (one id column serves both entities).
         */
        const mine = (rows ?? []).filter(
          (row) => String(row.crm_contact_id) === String(summary.crm_account_id)
        );
        if (mine.length === 0) {
          setTicketState("none");
          getAccountUrl(summary.system_id, summary.crm_account_id)
            .then((url) => {
              if (!cancelled && url) {
                setCrmUrls({ account: url });
              }
            })
            .catch(() => {
              // Links are a convenience; the record stands without them.
            });
          return null;
        }
        setDiscoveredChangeId(mine[0].id);
        getCRMURLForSuggestedChange(mine[0].id)
          .then((urls) => {
            if (!cancelled) {
              /*
               * For ACCOUNT-entity changes the endpoint returns the account's
               * deep link under the contact key, so it is re-homed here.
               */
              setCrmUrls({ account: urls?.account ?? urls?.contact });
            }
          })
          .catch(() => {
            // Links are a convenience; the record stands without them.
          });
        return loadExplain(mine[0].id).catch(() => setTicketState("none"));
      })
      .catch(() => {
        if (!cancelled) setTicketState("none");
      });
    return () => {
      cancelled = true;
    };
    /*
     * The anchor belongs in the deps: prev/next between two changes on one
     * account leaves the summary identical, so without it the panel would
     * keep the previous row's ticket.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary.system_id, summary.crm_account_id, props.anchorChangeId]);

  /* No Monitor pipeline exists for accounts; the model knows how to cope. */
  const model = useMemo(() => {
    if (explain) {
      const normalized = normalizeRecordReview(explain, null, crmUrls);
      const extras = accountExtraLedgerFields(summary, explain);
      /* An extra field with a pending change belongs in the ledger. */
      return {
        ...normalized,
        ledgerFields: [
          ...normalized.ledgerFields,
          ...extras.filter((field) => field.candidates.length > 0),
        ],
        fullRecordFields: [
          ...normalized.fullRecordFields,
          ...extras.filter((field) => field.candidates.length === 0),
        ],
      };
    }
    if (ticketState === "none") {
      const normalized = normalizeRecordReview(
        synthesizeAccountExplain(summary),
        null,
        crmUrls
      );
      return {
        ...normalized,
        fullRecordFields: [
          ...normalized.fullRecordFields.map((field) =>
            summaryCarriesField(summary, field.fieldName)
              ? field
              : { ...field, valueUnknown: true }
          ),
          ...accountExtraLedgerFields(summary, null),
        ],
      };
    }
    return null;
  }, [ticketState, explain, crmUrls, summary]);

  const settled = explain !== null || ticketState === "none";
  const enrichConfigured = props.systems.length > 0;
  const { onSettledChange } = props;
  useEffect(() => {
    onSettledChange?.(settled);
  }, [settled, onSettledChange]);

  return (
    <div className={classes.overviewPanel}>
      {!settled && (
        <div className={classes.overviewLoading}>
          <CirromLoadingLogo />
        </div>
      )}
      {model && (
        <RecordReviewCard
          embedded
          model={model}
          integratedSystemId={summary.system_id}
          recordId={summary.crm.id}
          suggestedChangeId={activeChangeId}
          unavailableSources={[]}
          onBusyChange={props.onBusyChange}
          commitActionsContainer={props.commitActionsContainer}
          onChanged={(decidedChangeIds) => {
            if (activeChangeId) {
              loadExplain(activeChangeId).catch(() => {
                // The card stands on the last good payload.
              });
            }
            /* The host grid drops the rows these ids belong to. */
            props.onChanged?.(decidedChangeIds);
          }}
        />
      )}
      {/*
       * With no source tabs beside Overview, the strip alone cannot say why -
       * the absence is a configuration fact, so it is stated.
       */}
      {settled && !enrichConfigured && (
        <div className={classes.overviewNote}>
          <FieldGroup label="Enrichment">
            <SectionEmptyState message="No enrichment is set up for this project." />
          </FieldGroup>
        </div>
      )}
    </div>
  );
};

/*
 * The selected source's knowledge, rendered under its tab. The tab strip
 * already carries the source's mark and name, so the panel is body only: no
 * repeated header, no second border inside the card.
 */
const SourcePanel = (props: {
  system: AccountSummaryEnrichSystem;
  summary: AccountSummary;
}) => {
  const { system } = props;
  /*
   * Resolved here because this is the lowest component that knows the CRM's
   * id; the label then rides down to the delta annotations, which are the
   * only place the panel speaks about the CRM side of a comparison.
   */
  const crmLabel = useCrmVendor(props.summary.system_id)?.label ?? "CRM";

  const matched = system.linkage_matched ?? [];
  const proposals = system.linkage_proposals ?? [];

  return (
    <div className={classes.sectionBody}>
      {!system.in_scope ? (
        /*
         * Out of scope is a different fact from "no matches": the account
         * exists in the CRM but the project's scope filters exclude it, so
         * both linkage lists are empty by construction.
         */
        <SectionEmptyState message="This account is outside the enrich scope for this project, so no matches are searched for." />
      ) : (
        <>
          {matched.length > 0 ? (
            matched.map((row) => (
              <EnrichTabContents
                key={`matched-${row.proposal_id}`}
                row={row}
                systemName={system.enrich_system_name}
                crmCompanyName={props.summary.crm.company}
                crmLabel={crmLabel}
              />
            ))
          ) : (
            <SectionEmptyState message="No reference record is linked to this account yet." />
          )}
          <FieldGroup
            label={
              proposals.length > 0
                ? `Proposed matches (${proposals.length})`
                : "Proposed matches"
            }
          >
            {proposals.length > 0 ? (
              <>
                <div className={classes.linkageList}>
                  {proposals.map((row) => (
                    <ProposalRow
                      key={`proposal-${row.proposal_id}`}
                      row={row}
                    />
                  ))}
                </div>
                {/*
                 * Read-only this iteration: proposals are reviewed and
                 * accepted on the linkage surface, so the answer to "where
                 * do I deal with these?" is a link rather than a button.
                 */}
                {system.data_project_id != null && (
                  <Link
                    variant="body2"
                    component={RouterLink}
                    to={`/data-project/enrich/${system.data_project_id}/linkage/Proposals`}
                    color="text.secondary"
                    underline="always"
                  >
                    Review proposals in linkage
                  </Link>
                )}
              </>
            ) : (
              <SectionEmptyState message="No proposed matches are waiting for review." />
            )}
          </FieldGroup>
        </>
      )}
    </div>
  );
};

/*
 * The tab label is the source's own mark, like the vendor panels this surface
 * descends from; a system without a shipped logo keeps its name in text.
 * Monochrome-ink marks invert in dark mode rather than sitting on a white
 * plate; the flag rides with the asset. The tooltip names the mark - two sync
 * groups of the same vendor would otherwise be indistinguishable tabs.
 */
const SourceTabLabel = (props: { system: AccountSummaryEnrichSystem }) => {
  const { system } = props;
  const logo = resolveSourceLogo(
    system.enrich_system_name,
    system.enrich_system_type
  );
  const name = system.enrich_system_name || "Enrichment";
  const verdict = sourceTabVerdict(system);
  return (
    <Tooltip title={`${name} · ${verdict.words}`}>
      <span className={classes.tabLabel}>
        {logo ? (
          /* Decorative here: the Tab's aria-label carries name and verdict. */
          <Box
            component="img"
            src={logo.src}
            alt=""
            className={classes.sourceLogo}
            sx={(theme) => ({
              filter:
                logo.invertInDark && theme.palette.mode === "dark"
                  ? "invert(1)"
                  : "none",
            })}
          />
        ) : (
          <span>{name}</span>
        )}
        {
          /* Same success tone as the panel's Linked line - no new vocabulary. */
          verdict.linked && (
            <CheckCircleOutlineOutlinedIcon
              className={classes.tabVerdictIcon}
              sx={{ color: "success.main" }}
            />
          )
        }
        {verdict.proposals > 0 && (
          <Box
            component="span"
            className={classes.tabVerdictCount}
            sx={{ borderColor: "divider", color: "text.secondary" }}
          >
            {verdict.proposals}
          </Box>
        )}
      </span>
    </Tooltip>
  );
};

/**
 * AccountSummaryCard
 *
 * Everything known about one CRM account: an Overview tab embedding the
 * record-review surface (live approve/reject and ad-hoc writes when a
 * data-quality ticket anchors it, read-only otherwise), then one tab per
 * enrich system with the system's mark as the tab label. The source tabs are
 * read-only - proposals are acted on in linkage. Self-contained so it can
 * move from a page into a modal without relayout.
 */
export const AccountSummaryCard = (props: {
  summary: AccountSummary;
  /* Modal hosting only; the page leaves all of these undefined. */
  onClose?: () => void;
  onPreviousRowRequested?: () => void;
  onNextRowRequested?: () => void;
  /**
   * The change the modal was opened for. With it the Overview panel skips the
   * ticket hunt and anchors on the row the steward actually clicked - which is
   * also what lets prev/next re-anchor between two changes on one account.
   */
  anchorChangeId?: number | null;
  /** Decided change ids, forwarded from the embedded review card. */
  onChanged?: (decidedChangeIds?: number[]) => void;
  /**
   * True while the embedded review card has a CRM write in flight or a bulk
   * confirmation open. The card cannot decide what that should mean - a page
   * has nothing to guard - so it reports through onBusyChange and the host
   * acts, handing the answer back as `busy` so the header's own Close goes
   * quiet with the shell; a live X over a dead backdrop would read as a bug.
   */
  busy?: boolean;
  onBusyChange?: (busy: boolean) => void;
}) => {
  const { summary } = props;
  const { crm } = summary;
  const location = [crm.city, crm.stateorprovince, crm.country]
    .filter(Boolean)
    .join(", ");

  const systems = (summary.enrich.configured && summary.enrich.systems) || [];
  /*
   * Selection is remembered by sync group id, with "overview" - the
   * account's own record - as the default. The summary can change under it
   * (the page re-fetches on navigation), so a group id that no longer
   * exists falls back to Overview rather than an empty panel.
   */
  const [selectedTab, setSelectedTab] = useState<number | "overview">(
    "overview"
  );
  const activeSystem =
    typeof selectedTab === "number"
      ? systems.find((system) => system.sync_group_id === selectedTab)
      : undefined;
  /* One id keeps the tab, its panel, and their aria wiring in lockstep. */
  const activeTabId = activeSystem?.sync_group_id ?? "overview";
  const activeReadableType = activeSystem?.enrich_system_type
    ? toReadableText(activeSystem.enrich_system_type)
    : null;

  /*
   * Hosted in a modal, the card is content height and centred like the record
   * review card, and the Overview - the account's version of that card - sets
   * the height. The source tabs then take exactly that height and scroll
   * inside it, so switching tabs never resizes the surface and a source's
   * long record never makes the account read as bulkier than a contact.
   * The Overview's height is read each time the steward leaves it, and only
   * once its ticket lookup has settled (the loader is shorter than either
   * outcome), so a ledger that gains or loses rows moves the lock with it.
   * A synchronous read on the click rather than a ResizeObserver: the
   * observer's callbacks proved unreliable inside the modal, and the click
   * is the one moment the height is both final and still on screen. The
   * page host has no backdrop and no need for any of this, so it opts out;
   * the close button is what says the card is hosted.
   */
  const hosted = Boolean(props.onClose);
  const cardRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [overviewSettled, setOverviewSettled] = useState(false);
  const [lockedHeight, setLockedHeight] = useState(0);
  /*
   * The footer slot the Overview's commit bar renders into, so Reject all /
   * Approve all sit beside share/copy under one rule, as on the contact card.
   * A callback ref, so the card re-renders once the node exists.
   */
  const [commitActionsSlot, setCommitActionsSlot] =
    useState<HTMLDivElement | null>(null);
  const onOverview = !activeSystem;
  /*
   * Read at the moment the steward leaves the Overview - see the tab
   * strip's onChange - rather than when it settles: the dossier keeps
   * growing after settle (the CRM links arrive on their own request), and
   * the height that matters is the one the card actually had when the
   * strip was clicked.
   */
  const lockToOverview = () => {
    const panel = panelRef.current;
    if (hosted && onOverview && overviewSettled && panel) {
      /* The fractional height: offsetHeight rounds, and a rounded lock is a
       * 1px step between tabs. The entrance is a transform on an ancestor
       * that has finished by the time anyone clicks, so the rect is true. */
      setLockedHeight(panel.getBoundingClientRect().height);
    }
  };
  /*
   * Share / copy feedback. The record card has an inline alert strip because
   * its CRM writes already need one; this card writes nothing of its own, so
   * a snackbar is the lighter fit for a two-outcome utility.
   */
  const { enqueueSnackbar } = useSnackbar();

  return (
    <Card ref={cardRef} className={classes.card}>
      <CardHeader
        className={classes.cardHeader}
        avatar={<CirromLogoAvatar />}
        title={crm.company || summary.crm_account_id}
        titleTypographyProps={{ variant: "h3", component: "h1" }}
        subheader={
          <div className={classes.headerSubtitle}>
            {crm.website && (
              <span className={classes.headerFact}>
                <InsertLinkOutlinedIcon
                  className={classes.headerFactIcon}
                  sx={{ color: "info.main" }}
                />
                <Link
                  variant="body2"
                  href={toExternalUrl(crm.website)}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="text.secondary"
                  underline="always"
                >
                  {crm.website}
                </Link>
              </span>
            )}
            {crm.website && location && (
              <Divider orientation="vertical" variant="middle" flexItem />
            )}
            {location && (
              <span className={classes.headerFact}>
                <PlaceOutlinedIcon
                  className={classes.headerFactIcon}
                  sx={{ color: "info.main" }}
                />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {location}
                </Typography>
              </span>
            )}
          </div>
        }
        /*
         * Only rendered when the card is hosted in a modal. The embedded
         * review card suppresses its own nav, so the queue position and the
         * way out live on the account header instead.
         */
        action={
          props.onClose || props.onNextRowRequested ? (
            <div className={classes.headerNav}>
              {props.onClose && (
                <IconButton
                  onClick={props.onClose}
                  color="error"
                  disabled={props.busy}
                  aria-label="Close account summary"
                >
                  <CloseOutlinedIcon />
                </IconButton>
              )}
              {(props.onPreviousRowRequested || props.onNextRowRequested) && (
                <div className={classes.headerNavButtons}>
                  <IconButton
                    onClick={() => props.onPreviousRowRequested?.()}
                    color="info"
                    disabled={!props.onPreviousRowRequested}
                    aria-label="Previous account"
                    className={classes.headerNavButton}
                  >
                    <NavigateBefore />
                    <Typography variant="body2">Prev</Typography>
                  </IconButton>
                  <IconButton
                    onClick={() => props.onNextRowRequested?.()}
                    color="info"
                    disabled={!props.onNextRowRequested}
                    aria-label="Next account"
                    className={classes.headerNavButton}
                  >
                    <NavigateNext />
                    <Typography variant="body2">Next</Typography>
                  </IconButton>
                </div>
              )}
            </div>
          ) : undefined
        }
      />
      <Divider />
      {/*
       * Overview first and default, then one tab per connected source; the
       * strip's hairline doubles as the section rule, so the panel below
       * needs no border of its own. The active system's readable type rides
       * at the right end of the strip - suppressed when it just repeats the
       * name ("Lexology Compete" / LEXOLOGY_COMPETE).
       *
       * The strip sits outside the scroll area because it is the control for
       * what scrolls: reading to the bottom of Overview must not take the
       * way back with it.
       */}
      <div className={classes.tabStrip}>
        <Box
          className={classes.sourceTabsRow}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tabs
            value={activeTabId}
            onChange={(_event, value) => {
              lockToOverview();
              setSelectedTab(value as number | "overview");
            }}
            aria-label="Account summary sections"
            /*
             * A data project can back any number of sync groups and the
             * card is built to live in a modal; the standard variant clips
             * overflowing tabs with no recourse, so the strip scrolls.
             */
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 0,
              /* Let the strip shrink inside the flex row so it scrolls. */
              minWidth: 0,
              /*
               * Folder-shaped tabs, so a row of logos reads as controls:
               * each tab is a hairline-bordered, top-rounded folder on
               * the strip's rule. One fixed height for every tab - a text
               * label and a wordmark of any width sit centred in the same
               * folder, so the strip's top edge is a single line rather
               * than a skyline. Resting tabs sit recessed on the field
               * tone with a dimmed mark; the selected tab rises to paper
               * at full strength, with the amber indicator marking it on
               * the rule. Both tones are theme roles, so the recess
               * survives dark mode and every variant.
               */
              "& .MuiTabs-flexContainer": {
                gap: 1,
              },
              "& .MuiTab-root": {
                height: "36px",
                minHeight: 0,
                minWidth: 0,
                padding: (theme) => theme.spacing(0, 3),
                fontSize: "13px",
                fontWeight: 500,
                textTransform: "none",
                border: 1,
                borderColor: "divider",
                borderBottom: "none",
                borderTopLeftRadius: "4px",
                borderTopRightRadius: "4px",
                backgroundColor: "background.default",
                opacity: 0.65,
                "&:hover": { opacity: 1 },
                "&.Mui-selected": {
                  opacity: 1,
                  backgroundColor: "background.paper",
                },
              },
            }}
          >
            <Tab
              value="overview"
              id="account-source-tab-overview"
              aria-controls="account-source-tabpanel-overview"
              label="Overview"
            />
            {systems.map((system) => (
              <Tab
                key={system.sync_group_id}
                value={system.sync_group_id}
                id={`account-source-tab-${system.sync_group_id}`}
                aria-controls={`account-source-tabpanel-${system.sync_group_id}`}
                aria-label={sourceTabName(system)}
                label={<SourceTabLabel system={system} />}
              />
            ))}
          </Tabs>
          {activeReadableType &&
            activeReadableType.toLowerCase() !==
              (activeSystem.enrich_system_name || "").toLowerCase() && (
              <Chip
                size="small"
                variant="outlined"
                label={activeReadableType}
                className={classes.sourceTabsMeta}
              />
            )}
        </Box>
      </div>
      {/*
       * The scrolling half of the card. Its top padding is the strip's now,
       * so it is dropped here rather than stacked on the panel's own leading
       * gap. The side padding keeps a source panel's fields aligned with the
       * tabs above them; the Overview drops it, because the embedded review
       * card brings its own 16px and the two stacked read as an indent the
       * contact card does not have.
       */}
      <CardContent
        ref={panelRef}
        className={classes.cardScrollArea}
        sx={{ pt: 0, px: activeSystem ? undefined : 0 }}
        /* flex-basis follows height, and the shell's cap still shrinks it. */
        style={
          activeSystem && lockedHeight ? { height: lockedHeight } : undefined
        }
      >
        <div
          role="tabpanel"
          id={`account-source-tabpanel-${activeTabId}`}
          aria-labelledby={`account-source-tab-${activeTabId}`}
        >
          {activeSystem ? (
            <SourcePanel system={activeSystem} summary={summary} />
          ) : (
            <AccountOverviewPanel
              summary={summary}
              systems={systems}
              anchorChangeId={props.anchorChangeId}
              onChanged={props.onChanged}
              onBusyChange={props.onBusyChange}
              onSettledChange={setOverviewSettled}
              commitActionsContainer={commitActionsSlot}
            />
          )}
        </div>
      </CardContent>
      <Divider />
      {/*
       * The same two utilities the record card carries: the account leaves
       * Cirrom by email or as an image of this card, brand mark and all.
       */}
      <div className={classes.cardFooter}>
        <ShareCopyActions
          targetRef={cardRef}
          details={{
            entity: "account",
            name: crm.company || summary.crm_account_id,
            phone: crm.telephone,
            website: crm.website,
            location,
          }}
          onNotify={(text, severity) =>
            enqueueSnackbar(text, { variant: severity })
          }
        />
        <div ref={setCommitActionsSlot} />
      </div>
    </Card>
  );
};
